import type {
  AvatarConfig,
  DuelHistoryEntry,
  DuelReplay,
  DuelRosterPlaces,
  DuelRosterRecord,
  LeagueId,
  ProfileShowcaseSlot,
  RankedStanding,
} from '@shared/index'
import { api } from './http'

export type PublicProfilePayload = {
  username: string
  avatar: AvatarConfig
  level: number
  standing: RankedStanding | null
  /** Best league placed in during the beta. Absent on older servers. */
  betaLeague?: LeagueId | null
  places: DuelRosterPlaces
  record: DuelRosterRecord
  /** Sum of finished-duel durations already stored for this account. */
  playTimeMs: number
  showcase: ProfileShowcaseSlot[]
}

/**
 * Public page for one display name. 404 when the name is free.
 */
export function fetchPublicDuels(
  token: string,
  username: string,
): Promise<{ duels: DuelHistoryEntry[] }> {
  return api<{ duels: DuelHistoryEntry[] }>(
    `/account/public/${encodeURIComponent(username)}/duels`,
    { token },
  )
}

export function fetchReplay(token: string, id: string): Promise<DuelReplay> {
  return api<DuelReplay>(`/account/replays/${encodeURIComponent(id)}`, { token })
}

export function postAiReplay(
  token: string,
  body: DuelReplay & { intact: boolean },
): Promise<void> {
  return api<void>('/account/replays', {
    method: 'POST',
    token,
    body: JSON.stringify(body),
  })
}

export function fetchPublicProfile(
  token: string,
  username: string,
): Promise<PublicProfilePayload> {
  return api<PublicProfilePayload>(
    `/account/public/${encodeURIComponent(username)}`,
    { token },
  )
}

/**
 * Replace the signed-in foil case. The server drops prints you do not own,
 * and any extra copy past what the album holds of that foil.
 */
export function putShowcase(
  token: string,
  slots: ProfileShowcaseSlot[],
): Promise<{ showcase: ProfileShowcaseSlot[] }> {
  return api<{ showcase: ProfileShowcaseSlot[] }>('/account/showcase', {
    method: 'PUT',
    token,
    body: JSON.stringify({ slots }),
  })
}

export function publicProfilePath(username: string): string {
  return `/u/${encodeURIComponent(username)}`
}

export type LevelBoardEntry = {
  username: string
  avatar: AvatarConfig
  level: number
  xpIntoLevel: number
  xpToNext: number
}

export type LevelBoardYou = {
  /** 1-based among accounts with XP. Null until this account has earned any. */
  position: number | null
  level: number
  xpIntoLevel: number
  xpToNext: number
}

export type LevelBoardPayload = {
  entries: LevelBoardEntry[]
  total: number
  page: number
  pageSize: number
  you: LevelBoardYou
}

/**
 * One page of accounts with the highest level on the server.
 */
export function fetchLevelBoard(
  token: string,
  page = 1,
): Promise<LevelBoardPayload> {
  return api<LevelBoardPayload>(`/account/levels?page=${page}`, { token })
}

export const AI_WIN_LADDERS = ['hard', 'expert', 'master'] as const

export type AiWinLadder = (typeof AI_WIN_LADDERS)[number]

export type AiWinEntry = {
  username: string
  avatar: AvatarConfig
  wins: number
}

export type AiWinYou = {
  /** 1-based among accounts with at least one win. Null until then. */
  position: number | null
  wins: number
}

export type AiWinBoard = {
  entries: AiWinEntry[]
  total: number
  page: number
  pageSize: number
  you: AiWinYou
}

export type AiWinBoardsPayload = Record<AiWinLadder, AiWinBoard>

/**
 * One page of finished wins vs the CPU on Hard, Expert, and Master.
 */
export function fetchAiWinBoards(
  token: string,
  page = 1,
): Promise<AiWinBoardsPayload> {
  return api<AiWinBoardsPayload>(`/account/ai-wins?page=${page}`, { token })
}
